import {
  type AreaScope,
  areaScopeKey,
  pathInArea,
} from '../../contracts/account/product-area';
import type {
  FindingCapture,
  FindingOccurrence,
} from '../../contracts/evidence/finding-capture';
import {
  toolAttributionIsTrusted,
  LEGACY_UNKNOWN_VERSION,
  recordedOrUnknown,
  totalLost,
} from '../../contracts/evidence/finding-capture';

// ---------------------------------------------------------------------------
// Comparing two captures of one repository under one area scope.
//
// The whole difficulty is in what this refuses to say. A finding missing from
// the later capture has at least five explanations, and only one of them is
// "somebody fixed it":
//
//   - the tool that reported it did not run the second time;
//   - the second capture hit its cap and the finding fell outside it;
//   - the analyser or its configuration changed under us;
//   - the file moved out of the area's paths;
//   - it was fixed.
//
// Reporting the fifth when any of the first four is live is the one failure
// mode that actively costs a team work: it closes a problem nobody solved. So
// every blocker is named, and a blocked comparison answers `unknown` for the
// absent findings rather than `no-longer-observed`.
//
// Line movement is not a blocker and never appears here, because the logical
// finding id is line independent by construction. A finding that shifted forty
// lines compares as the same finding, still present.
// ---------------------------------------------------------------------------

export const COMPARISON_STATES = [
  'introduced',
  'still-present',
  'no-longer-observed',
  'unknown',
] as const;
export type ComparisonState = (typeof COMPARISON_STATES)[number];

/** Why a comparison could not license a disappearance claim. */
export const COMPARISON_BLOCKERS = [
  /** One or both captures cannot say which tools ran. */
  'completeness-unknown',
  /**
   * A tool that ran for the BASELINE did not run for the candidate.
   *
   * Blocks `no-longer-observed`: the candidate's silence may just be a tool
   * that stopped looking.
   */
  'tool-coverage-narrowed',
  /**
   * A tool that ran for the CANDIDATE did not run for the baseline.
   *
   * Blocks `introduced`, and is the exact mirror of the above. A finding the
   * candidate reports and the baseline does not may have been there all along
   * with nothing looking for it — reporting it as new is a regression nobody
   * caused.
   */
  'baseline-coverage-narrowed',
  /** A tool failed in either capture. */
  'tool-failed',
  /** The analyser configuration differs between the two captures. */
  'config-drift',
  /** The analyser version differs, or one capture did not record it. */
  'analyzer-drift',
  /** Either capture was cut by its own cap. */
  'capture-truncated',
  /** The two captures are of different repositories. */
  'repo-mismatch',
  /** The two captures were compared under different area scopes. */
  'scope-mismatch',
  /** Both sides name the same capture; there is nothing to compare. */
  'same-capture',
  /** A capture dropped findings before they were ever stored. */
  'evidence-dropped',
  /** Rows were stored but could not be read back. */
  'evidence-unreadable',
  /** A tool's version is unknown on one or both sides, so drift is unknown. */
  'tool-version-unrecorded',
  /**
   * One or both captures did not observe the rules they ran under.
   *
   * SHARED, blocking BOTH directions. Two captures whose effective
   * configuration was never observed have not been shown to have used the
   * same rules — their config digests can match while the rules behind them
   * differ, so neither a disappearance nor an introduction is attributable to
   * the repository rather than to a rule that changed underneath.
   */
  'rules-unobserved',
  /**
   * A capture never stated how many findings its analyser produced.
   *
   * Blocks that SIDE's absence only: the set may be short by any amount and
   * nothing recorded says by how much.
   */
  'total-unrecorded',
] as const;
export type ComparisonBlocker = (typeof COMPARISON_BLOCKERS)[number];

export interface ComparedFinding {
  readonly findingId: string;
  readonly state: ComparisonState;
  /**
   * Whether the rules that produce THIS finding were established on both
   * sides.
   *
   * `false` is why a row is `unknown` when nothing else about the pair is
   * wrong: the tool that reports it is configured in a way this scan could
   * not read, so its silence is not evidence. Carried per finding because
   * the answer differs per tool within one capture.
   */
  readonly rulesObserved: boolean;
  readonly ruleId: string;
  readonly path: string;
  readonly severity: FindingOccurrence['severity'];
  readonly category: FindingOccurrence['category'];
  /** How many occurrences each side saw. Multiplicity is part of the answer. */
  readonly baselineOccurrences: number;
  readonly candidateOccurrences: number;
  /**
   * Whether the count moved while the finding stayed present.
   *
   * A rule that fired eleven times and now fires three is progress that
   * `still-present` alone cannot express, and it is never a fix.
   */
  readonly occurrenceDelta: number;
  /** Where it is now, or where it last was. */
  readonly lines: readonly number[];
  /** The exact occurrence ids on each side, for pinning a later read. */
  readonly baselineOccurrenceIds: readonly string[];
  readonly candidateOccurrenceIds: readonly string[];
}

export interface CaptureComparison {
  readonly kind: 'compared' | 'incomparable';
  readonly baselineCaptureId: string;
  readonly candidateCaptureId: string;
  readonly baselineHeadSha: string;
  readonly candidateHeadSha: string;
  readonly scopeKey: string;
  readonly scope: AreaScope;
  /**
   * Whether a disappearance may be reported as a disappearance at all.
   *
   * False whenever any blocker is live, in which case every finding the
   * candidate did not report is `unknown`.
   */
  /**
   * Whether the PAIR licenses a disappearance claim.
   *
   * Pair-level only, and not sufficient on its own: a finding also needs its
   * OWN tool's rules to have been established, which
   * `ComparedFinding.rulesObserved` records. The two are separate because
   * they fail separately — a capped baseline stops every claim in the
   * comparison, while an unparsed Ruff configuration stops only the claims
   * that rest on Ruff.
   */
  readonly absenceIsMeaningful: boolean;
  /**
   * Whether EVERY tool on both sides had its rules established.
   *
   * `false` means some findings here are `unknown` for a reason that is not
   * about the pair at all. `toolsWithUnobservedRules` names which.
   */
  readonly allRulesObserved: boolean;
  /** The tools whose rules were not established, on either side. */
  readonly unobservedRuleTools: readonly string[];
  /**
   * Whether the BASELINE can be trusted to say a finding was absent from it.
   *
   * Separate from the above, because they license opposite claims: this one
   * licenses `introduced`, the other licenses `no-longer-observed`. A
   * comparison can easily support one and not the other, and collapsing them
   * into a single flag is how a finding that was always there comes to be
   * reported as a new regression.
   */
  readonly baselineAbsenceIsMeaningful: boolean;
  readonly blockers: readonly ComparisonBlocker[];
  readonly findings: readonly ComparedFinding[];
  readonly counts: Readonly<Record<ComparisonState, number>>;
}

/** One side of a comparison, with whatever the read of it lost. */
export interface ComparisonSide {
  readonly capture: FindingCapture;
  readonly occurrences: readonly FindingOccurrence[];
  /**
   * Rows that were stored and could not be read back — a payload that no
   * longer parses, most likely.
   *
   * Dropping such a row silently and keeping the capture's completeness would
   * make the set look whole when it is not, and the missing finding would
   * then read as fixed. It is counted and it blocks.
   */
  readonly unreadable?: number | undefined;
}

export interface CompareCapturesInput {
  readonly baseline: Readonly<ComparisonSide>;
  readonly candidate: Readonly<ComparisonSide>;
  readonly scope: AreaScope;
}

function toolMap(
  capture: Readonly<FindingCapture>,
): ReadonlyMap<string, FindingCapture['tools'][number]['status']> {
  return new Map(capture.tools.map((tool) => [tool.name, tool.status]));
}

/**
 * Every reason this pair cannot support a disappearance claim.
 *
 * All of them, not the first: a reader who resolves one and re-runs must not
 * discover a second that was there the whole time.
 */
export function blockersFor(
  input: Readonly<{
    baseline: ComparisonSide;
    candidate: ComparisonSide;
    scope: AreaScope;
  }>,
): readonly ComparisonBlocker[] {
  return [
    ...pairingBlockers(
      input.baseline.capture,
      input.candidate.capture,
      input.scope,
    ),
    ...sideBlockers(input.baseline),
    ...sideBlockers(input.candidate),
    ...driftBlockers(input.baseline.capture, input.candidate.capture),
    // Still reported, because it is still true that SOME rules were not
    // established — but it no longer poisons every direction. Which findings
    // it actually reaches is decided per tool, on the finding.
    ...(rulesUnobserved(input.baseline.capture) ||
    rulesUnobserved(input.candidate.capture)
      ? (['rules-unobserved'] as const)
      : []),
    ...(coverageNarrowed(input.candidate.capture, input.baseline.capture)
      ? (['tool-coverage-narrowed'] as const)
      : []),
    ...(coverageNarrowed(input.baseline.capture, input.candidate.capture)
      ? (['baseline-coverage-narrowed'] as const)
      : []),
  ]
    .filter((blocker, index, all) => all.indexOf(blocker) === index)
    .toSorted();
}

/** Blockers about WHICH two things are being compared. */
function pairingBlockers(
  baseline: Readonly<FindingCapture>,
  candidate: Readonly<FindingCapture>,
  scope: Readonly<AreaScope>,
): readonly ComparisonBlocker[] {
  return [
    ...(baseline.repoKey.toLowerCase() === candidate.repoKey.toLowerCase()
      ? []
      : (['repo-mismatch'] as const)),
    ...(baseline.repoKey.toLowerCase() === scope.repoKey.toLowerCase()
      ? []
      : (['scope-mismatch'] as const)),
    ...(baseline.captureId === candidate.captureId
      ? (['same-capture'] as const)
      : []),
  ];
}

/**
 * Blockers one SIDE raises on its own, whatever it is compared with.
 *
 * These are the reasons that side cannot be trusted to say a finding was
 * absent from it — which is what both `introduced` and `no-longer-observed`
 * ultimately rest on.
 */
function completenessUnknown(capture: Readonly<FindingCapture>): boolean {
  return (
    capture.completeness.kind === 'unknown' ||
    capture.sourceCoverage?.complete === false ||
    (capture.evidenceProfile !== undefined &&
      capture.sourceCoverage === undefined)
  );
}

export function sideBlockers(
  side: Readonly<ComparisonSide>,
): readonly ComparisonBlocker[] {
  const { capture } = side;
  return [
    ...(completenessUnknown(capture)
      ? (['completeness-unknown'] as const)
      : []),
    ...(capture.completeness.reasons.includes('capture-capped') ||
    capture.completeness.reasons.includes('transport-capped') ||
    // A capture cut to fit the store's per-document ceiling is short by
    // exactly as much as one cut by the occurrence cap, and its silence
    // about a finding is worth exactly as little.
    capture.completeness.reasons.includes('document-budget-capped')
      ? (['capture-truncated'] as const)
      : []),
    ...(capture.tools.some((tool) => tool.status === 'failed')
      ? (['tool-failed'] as const)
      : []),
    // Anything the capture itself records as lost. `non-relative-paths-dropped`
    // used to be ignored here: a finding whose path the scanner reported
    // absolutely was dropped at the producer, and its absence from this side
    // says nothing about the repository.
    ...(totalLost(capture.loss) > 0 ? (['evidence-dropped'] as const) : []),
    ...((side.unreadable ?? 0) > 0 ? (['evidence-unreadable'] as const) : []),
    // The producer never said how many findings it made and the list arrived
    // at the transport bound, so this side may be short by any amount. Its
    // silence about a finding is not evidence.
    ...(capture.completeness.reasons.includes('total-unrecorded')
      ? (['total-unrecorded'] as const)
      : []),
  ];
}

/**
 * Whether the two sides ran under the same configuration.
 *
 * The digest string is the fast answer and no longer the only one. It is an
 * opaque encoding, and two dialects could spell one string for two different
 * readings — a `2` tool version of literally `3~9.0` and a `3` tool version
 * of `9.0` produced identical bytes — so equality there is checked against
 * the provenance it is supposed to summarise: the same tools, each with the
 * same RECORDED version, read conservatively for the dialect that wrote it.
 * A disagreement is drift whatever the digests say.
 */
function configAgrees(
  baseline: Readonly<FindingCapture>,
  candidate: Readonly<FindingCapture>,
): boolean {
  if (
    baseline.configVersion !== candidate.configVersion ||
    baseline.evidenceProfile !== candidate.evidenceProfile
  )
    return false;
  const versions = (
    capture: Readonly<FindingCapture>,
  ): ReadonlyMap<string, string | null> =>
    new Map(
      capture.tools.map((tool) => [
        tool.name,
        recordedOrUnknown(
          tool.version,
          capture.version,
          LEGACY_UNKNOWN_VERSION,
        ),
      ]),
    );
  const before = versions(baseline);
  const after = versions(candidate);
  if (before.size !== after.size) return false;
  return [...before].every(
    ([name, version]) => after.has(name) && after.get(name) === version,
  );
}

/** Whether a capture observed the rules it ran under. */
function rulesUnobserved(capture: Readonly<FindingCapture>): boolean {
  return (
    capture.completeness.reasons.includes('rules-unobserved') ||
    capture.completeness.reasons.includes('rules-partially-observed')
  );
}

/** Blockers about the two captures having been taken by different things. */
function driftBlockers(
  baseline: Readonly<FindingCapture>,
  candidate: Readonly<FindingCapture>,
): readonly ComparisonBlocker[] {
  // An unrecorded version on either side is drift, not agreement: two nulls do
  // not establish that one scanner produced both readings.
  const analyzerDrift =
    baseline.analyzerVersion === null ||
    candidate.analyzerVersion === null ||
    baseline.analyzerVersion !== candidate.analyzerVersion;
  // A tool whose version neither side recorded is a rule set that may have
  // moved without anything here noticing. Its own blocker rather than silence:
  // "we cannot tell whether the rules changed" is a different statement from
  // "the rules changed", and a reader resolving drift needs to know which.
  //
  // A pre-`3` capture cannot tell a null version from a tool whose version
  // IS the sentinel that encoding wrote for null — the two are the same
  // bytes — so such a value establishes nothing and is read as unrecorded.
  // Under `3` they are structurally distinct and the value stands.
  const versionUnrecorded = [
    ...baseline.tools.map((tool) => ({ tool, encoding: baseline.version })),
    ...candidate.tools.map((tool) => ({ tool, encoding: candidate.version })),
  ].some(
    ({ tool, encoding }) =>
      tool.status === 'ok' &&
      recordedOrUnknown(tool.version, encoding, LEGACY_UNKNOWN_VERSION) ===
        null,
  );
  return [
    ...(configAgrees(baseline, candidate) ? [] : (['config-drift'] as const)),
    ...(analyzerDrift ? (['analyzer-drift'] as const) : []),
    ...(versionUnrecorded ? (['tool-version-unrecorded'] as const) : []),
  ];
}

/**
 * The tools whose rules were NOT established, on either side.
 *
 * Per tool, because that is the only scale at which the statement is true. A
 * single Python tool configured in a format the observer does not parse used
 * to make the whole capture `rules-unobserved`, and that blocked every
 * finding in the scan — including the scanner's own suppression audit, whose
 * rules are its own source and were never in question. Two partially
 * observed configurations can still present identical digests while their
 * unobserved halves differ, so the caution is right; it just belongs to the
 * tool it is about.
 *
 * A tool that recorded nothing (`null`) is counted as unobserved: not
 * recorded is not observed, and a capture written before this existed must
 * not be read as though it had established something.
 */
function toolsWithUnobservedRules(
  baseline: Readonly<FindingCapture>,
  candidate: Readonly<FindingCapture>,
  namedByFindings: ReadonlySet<string | null>,
): ReadonlySet<string> {
  const onBaseline = affirmativelyObserved(baseline);
  const onCandidate = affirmativelyObserved(candidate);
  const unobserved = new Set<string>();
  // From the tools the EVIDENCE actually names, so the list answers "which
  // of these findings cannot be read" rather than naming tools no finding
  // here came from.
  for (const tool of namedByFindings) {
    if (tool === null) {
      unobserved.add('(unattributed)');
      continue;
    }
    if (!onBaseline.has(tool) || !onCandidate.has(tool)) unobserved.add(tool);
  }
  // Plus any tool that RAN on a side without its rules established: it could
  // have produced findings that are simply absent from this scope, and a
  // reader resolving the list needs to see it.
  for (const tool of [...baseline.tools, ...candidate.tools]) {
    if (tool.status !== 'ok') continue;
    if (!onBaseline.has(tool.name) || !onCandidate.has(tool.name)) {
      unobserved.add(tool.name);
    }
  }
  return unobserved;
}

/**
 * The tools a capture AFFIRMATIVELY established the rules of.
 *
 * A whitelist, not a blacklist, and the difference is the whole point. The
 * blacklist named tools a capture had recorded as unobserved — so a tool the
 * capture said NOTHING about was absent from it and therefore licensed. Two
 * captures recording provenance only for ESLint would report a Ruff finding
 * as `no-longer-observed`, on the strength of never having mentioned Ruff.
 *
 * Only a tool that ran to completion AND whose rules were established gets
 * in. Everything else — skipped, failed, unrecorded, explicitly unobserved —
 * is absent, and absence is unknown.
 */
function affirmativelyObserved(
  capture: Readonly<FindingCapture>,
): ReadonlySet<string> {
  const observed = new Set<string>();
  // A capture minted under the encoding that left the tool OUT of an
  // occurrence's identity cannot be asked which analyser found something and
  // believed: two payloads differing only in that attribution encoded
  // identically, so the answer was never pinned to anything. Every finding
  // in such a capture is unreadable for rules purposes rather than readable
  // on trust — which is the safe direction, and the one that stops the
  // legacy format being a way back into the swap this closed.
  if (!toolAttributionIsTrusted(capture.version)) return observed;
  for (const tool of capture.tools) {
    if (tool.status === 'ok' && tool.rulesObserved === true) {
      observed.add(tool.name);
    }
  }
  return observed;
}

/**
 * Whether `side` looked with a NARROWER suite than `other` did.
 *
 * Absence is only evidence from a side that actually looked. A tool that ran
 * for one side and was skipped, failed or simply omitted on the other leaves
 * that other side silent about everything that tool reports — and its silence
 * must not be read as the finding being absent.
 *
 * Directional on purpose. Both sides skipping the same tool is NOT narrowing:
 * neither looked, they looked equally, and a finding one of them did report is
 * still evidence about the tools that did run.
 */
export function coverageNarrowed(
  side: Readonly<FindingCapture>,
  other: Readonly<FindingCapture>,
): boolean {
  const mine = toolMap(side);
  return other.tools.some(
    (tool) => tool.status === 'ok' && mine.get(tool.name) !== 'ok',
  );
}

function groupInScope(
  side: Readonly<ComparisonSide>,
  scope: Readonly<AreaScope>,
): ReadonlyMap<string, readonly FindingOccurrence[]> {
  const grouped = new Map<string, readonly FindingOccurrence[]>();
  for (const occurrence of side.occurrences) {
    if (!pathInArea(occurrence.path, scope.pathPrefixes)) continue;
    grouped.set(occurrence.findingId, [
      ...(grouped.get(occurrence.findingId) ?? []),
      occurrence,
    ]);
  }
  return grouped;
}

/** The descriptive fields, taken from whichever side actually saw it. */
function describe(
  before: readonly FindingOccurrence[],
  after: readonly FindingOccurrence[],
): Pick<ComparedFinding, 'ruleId' | 'path' | 'severity' | 'category'> {
  const representative = after[0] ?? before[0];
  return {
    ruleId: representative?.ruleId ?? '',
    path: representative?.path ?? '',
    severity: representative?.severity ?? 'info',
    category: representative?.category ?? 'lint',
  };
}

/** Where it is now, or — when it is gone — where it last was. */
function linesOf(
  before: readonly FindingOccurrence[],
  after: readonly FindingOccurrence[],
): readonly number[] {
  return (after.length > 0 ? after : before)
    .flatMap((occurrence) =>
      occurrence.line === null ? [] : [occurrence.line],
    )
    .toSorted((a, b) => a - b);
}

/** Which of the three states one finding is in, and what each side saw. */
function compareOne(
  input: Readonly<{
    findingId: string;
    before: readonly FindingOccurrence[];
    after: readonly FindingOccurrence[];
    baselineAbsenceIsMeaningful: boolean;
    candidateAbsenceIsMeaningful: boolean;
    observedOnBaseline: ReadonlySet<string>;
    observedOnCandidate: ReadonlySet<string>;
  }>,
): ComparedFinding {
  const { before, after } = input;
  // Whose rules produced this finding, and were they established? A finding
  // whose tool is unnamed is treated as unobserved: unknown provenance is
  // not a licence, and the conservative reading is the only safe one.
  const tools = [...before, ...after].map((occurrence) => occurrence.tool);
  // AFFIRMATIVE on both sides. A finding whose tool neither capture recorded
  // anything about is unknown, not licensed: "we never mentioned that tool"
  // is not an observation of its rules.
  const rulesObserved =
    tools.length > 0 &&
    tools.every(
      (tool) =>
        tool !== null &&
        input.observedOnBaseline.has(tool) &&
        input.observedOnCandidate.has(tool),
    );
  return {
    findingId: input.findingId,
    rulesObserved,
    state: rulesObserved
      ? stateOf({
          beforeCount: before.length,
          afterCount: after.length,
          baselineAbsenceIsMeaningful: input.baselineAbsenceIsMeaningful,
          candidateAbsenceIsMeaningful: input.candidateAbsenceIsMeaningful,
        })
      : // Its rule set may have moved without anything here noticing, so
        // neither its disappearance nor its appearance means anything. Still
        // reported, as `unknown`, with the reason on the row.
        stillPresentOrUnknown(before.length, after.length),
    ...describe(before, after),
    baselineOccurrences: before.length,
    candidateOccurrences: after.length,
    occurrenceDelta: after.length - before.length,
    lines: linesOf(before, after),
    baselineOccurrenceIds: before.map((occurrence) => occurrence.occurrenceId),
    candidateOccurrenceIds: after.map((occurrence) => occurrence.occurrenceId),
  };
}

/**
 * Which state one finding is in, given what each side can be trusted to say.
 *
 * TWO directions need licensing, not one, and the first version of this only
 * licensed the second:
 *
 *  - "no longer observed" claims the candidate looked and did not find it. It
 *    needs the CANDIDATE to be trustworthy about absence.
 *  - "introduced" claims the baseline looked and did not find it — that is
 *    what makes it new. It needs the BASELINE to be trustworthy about absence.
 *
 * A baseline that hit its cap, lost rows, or ran a narrower tool set is not
 * trustworthy about absence. A finding that was always there but fell outside
 * that baseline's cap would otherwise be reported as `introduced`, which is a
 * regression nobody caused — the exact mirror of reporting a fix nobody made,
 * and just as expensive to act on.
 *
 * So each direction consults its own side, and a finding the other side
 * cannot speak for is `unknown`.
 */
/**
 * The only two states a finding with unestablished rules may take.
 *
 * Present on both sides is still `still-present` — that claim rests on both
 * scans having SEEN it, not on either having looked properly for something
 * absent. Anything else is `unknown`.
 */
function stillPresentOrUnknown(
  beforeCount: number,
  afterCount: number,
): ComparisonState {
  return beforeCount > 0 && afterCount > 0 ? 'still-present' : 'unknown';
}

function stateOf(
  input: Readonly<{
    beforeCount: number;
    afterCount: number;
    baselineAbsenceIsMeaningful: boolean;
    candidateAbsenceIsMeaningful: boolean;
  }>,
): ComparisonState {
  if (input.afterCount > 0) {
    // Present now. Whether that is NEW depends on the baseline's absence.
    if (input.beforeCount > 0) return 'still-present';
    return input.baselineAbsenceIsMeaningful ? 'introduced' : 'unknown';
  }
  // Absent now. Whether that is PROGRESS depends on the candidate's absence.
  return input.candidateAbsenceIsMeaningful ? 'no-longer-observed' : 'unknown';
}

/**
 * Compare two captures of one repository within one area scope.
 *
 * Both captures are filtered to the scope FIRST, so a finding outside the area
 * never appears on either side — it is not this area's evidence and its
 * absence is not this area's progress.
 */
export function compareCaptures(
  input: Readonly<CompareCapturesInput>,
): CaptureComparison {
  const blockers = blockersFor(input);
  // Shared reasons — a drifted analyser, a mismatched scope — poison BOTH
  // directions. A side's own reasons poison only what that side can say.
  const shared = [
    ...pairingBlockers(
      input.baseline.capture,
      input.candidate.capture,
      input.scope,
    ),
    ...driftBlockers(input.baseline.capture, input.candidate.capture),
  ];
  // Each direction consults its OWN side's blockers plus whether that side
  // looked with a narrower suite than the other. A tool skipped on the
  // baseline and run on the candidate makes the baseline silent about
  // everything that tool reports — so a candidate-only finding may have been
  // there all along, and calling it `introduced` is a regression nobody
  // caused. This is structural: it does not depend on a version happening to
  // be unrecorded.
  const baselineAbsenceIsMeaningful =
    shared.length === 0 &&
    sideBlockers(input.baseline).length === 0 &&
    !coverageNarrowed(input.baseline.capture, input.candidate.capture);
  const candidateAbsenceIsMeaningful =
    shared.length === 0 &&
    sideBlockers(input.candidate).length === 0 &&
    !coverageNarrowed(input.candidate.capture, input.baseline.capture);

  const baseline = groupInScope(input.baseline, input.scope);
  const candidate = groupInScope(input.candidate, input.scope);
  const observedOnBaseline = affirmativelyObserved(input.baseline.capture);
  const observedOnCandidate = affirmativelyObserved(input.candidate.capture);

  const findingIds = [
    ...new Set([...baseline.keys(), ...candidate.keys()]),
  ].toSorted();

  const findings = findingIds.map((findingId) =>
    compareOne({
      findingId,
      before: baseline.get(findingId) ?? [],
      after: candidate.get(findingId) ?? [],
      baselineAbsenceIsMeaningful,
      candidateAbsenceIsMeaningful,
      observedOnBaseline,
      observedOnCandidate,
    }),
  );

  const namedByFindings = new Set<string | null>(
    [...baseline.values(), ...candidate.values()].flatMap((occurrences) =>
      occurrences.map((occurrence) => occurrence.tool),
    ),
  );
  const unobservedTools = toolsWithUnobservedRules(
    input.baseline.capture,
    input.candidate.capture,
    namedByFindings,
  );

  const counts = {
    introduced: 0,
    'still-present': 0,
    'no-longer-observed': 0,
    unknown: 0,
  };
  for (const finding of findings) counts[finding.state] += 1;

  return {
    kind: blockers.length === 0 ? 'compared' : 'incomparable',
    baselineCaptureId: input.baseline.capture.captureId,
    candidateCaptureId: input.candidate.capture.captureId,
    baselineHeadSha: input.baseline.capture.headSha,
    candidateHeadSha: input.candidate.capture.headSha,
    scopeKey: areaScopeKey(input.scope),
    scope: input.scope,
    absenceIsMeaningful: candidateAbsenceIsMeaningful,
    baselineAbsenceIsMeaningful,
    allRulesObserved: unobservedTools.size === 0,
    unobservedRuleTools: [...unobservedTools].toSorted(),
    blockers,
    findings,
    counts,
  };
}
